/* eslint-disable @typescript-eslint/no-unused-vars */

import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  FormControl, FormGroupDirective,
  FormsModule, NgForm,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { IBiographic, IBiographicPatch } from '../biographics.model';
import {
  BirthDateValidator,
  ChildSpecifierValidator,
  GenderValidator,
  makeTestDateValidator
} from './biographic-validators';
import { LocalScanningWebApiService } from '../../../services/local-scanning-web-api.service';
import { PendingCollectionService } from '../../../services/pending-collection.service';
import { GENDER_MAP, Pending } from '../pending.model';
import { catchError, finalize, switchMap, take } from 'rxjs/operators';
import { PreviousRouteService } from '../../shared/previous-route.service';
import { SpinnerService, ThrottleClickDirective } from '@drc-eca/eca-lrc-common-ui';
import { Observable } from 'rxjs';
import { EcaDatepickerCalendarHeaderComponent } from '@drc-eca/eca-components-lib';
import { EcaMultiSelectModule } from '@drc-eca/eca-components-lib';
import { AsyncPipe } from '@angular/common';
import {
  DefaultFlexDirective,
  DefaultLayoutAlignDirective,
  DefaultLayoutDirective,
  DefaultLayoutGapDirective
} from '@ngbracket/ngx-layout/flex';
import { MatError, MatFormField, MatInput, MatLabel, MatSuffix } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepickerToggleIcon
} from '@angular/material/datepicker';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { filterEmptyValue } from '../../../rxjs-operators';
import { SetupConfigService } from '../../../services/setup-config.service';
import { FormSelectOptions, FormSelectOption } from '../../../services/setup-config.service';
import { MasterForm, MasterFormInput, MasterFormPage } from '../../alerts/review/master-forms.model';
import { flatMap } from 'lodash';

@Component({
  selector: 'eca-biographics-edit',
  templateUrl: './edit.component.html',
  styleUrls: ['./edit.component.scss'],
  imports: [FormsModule, ReactiveFormsModule, DefaultLayoutDirective, DefaultLayoutAlignDirective, DefaultFlexDirective, MatFormField, MatLabel, MatInput, MatError, MatSelect, MatOption, MatDatepickerInput, MatDatepickerToggle, MatSuffix, MatIcon, MatDatepickerToggleIcon, MatDatepicker, DefaultLayoutGapDirective, MatButton, RouterLink, ThrottleClickDirective, AsyncPipe, EcaMultiSelectModule]
})
export class BiographicEditComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private builder = inject(UntypedFormBuilder);
  private pendingCollection = inject(PendingCollectionService);
  private api = inject(LocalScanningWebApiService);
  private setupConfig = inject(SetupConfigService);
  previousRouteService = inject(PreviousRouteService);
  spinnerService = inject(SpinnerService);

  ecaCalendarHeader = EcaDatepickerCalendarHeaderComponent;

  form: UntypedFormGroup;
  biographic: IBiographic;
  pendingId: string;
  pending: Pending;
  masterForm: MasterForm;

  previousUrl: string;
  submitText: string;

  spinner$: Observable<boolean>;
  formSelectOptions: FormSelectOptions;

  ngOnInit() {
    const {pendingId} = this.route.snapshot.parent.params;
    this.pendingId = pendingId;

    this.loadFormSelectOptionsFromSetupConfig();

    // Load pending record and masterForm definition
    this.pendingCollection.getPending(pendingId).pipe(
      switchMap((pending: Pending) => {
        const materialId = pending.sortKey.split('-')[0];

        return this.api.getMasterForm(materialId, pending).pipe(
          switchMap((masterForm) => {
            return new Observable(observer => {
              observer.next({pending, masterForm});
              observer.complete();
            });
          })
        );
      })
    ).subscribe((result: { pending: Pending, masterForm: MasterForm }) => {
      this.pending = result.pending;
      this.masterForm = result.masterForm;
      this.biographic = this.getFormattedBiographic(result.pending);

      // Build the form controls and patch the values
      this.buildForm();
      this.form.patchValue(this.biographic);
    });

    this.previousRouteService.navigationUrls$.subscribe((navUrls) => {
      const newRouteLink = this.previousRouteService.processBiographicsAlertsRouteEvent(navUrls);
      this.previousUrl = newRouteLink.newPreviousUrl;
      this.submitText = newRouteLink.newLinkLabelText;
    });

    this.spinner$ = this.spinnerService.spinner.asObservable();
  }

  // Returns an array of all inputs defined on the form
  getAllFormInputs(): MasterFormInput[] {
    return flatMap(this.masterForm.pages, (page: MasterFormPage) => page.inputs);
  }

  // Returns an array of inputs by demographicGroup
  getFormInputsByDemographicGroup(demographicGroup: string): MasterFormInput[] {
    return this.getAllFormInputs().filter(input => input.demographicGroup === demographicGroup);
  }

  // Checks if the pending record has a specific extension
  hasExtension(extensionName: string): boolean {
    return this.getAllFormInputs().some(input => input.name === extensionName && input.isExtension);
  }

  // Checks if the form definition has any inputs defined with the matching demographicGroup
  hasDemographicGroup(demographicGroup: string): boolean {
    return this.getFormInputsByDemographicGroup(demographicGroup).length > 0;
  }

  // Returns an array of extension names assigned to a demographicGroup
  getExtensionNamesInDemographicGroup(demographicGroup: string): string[] {
    return this.getFormInputsByDemographicGroup(demographicGroup).map(input => input.name);
  }

  // Returns a boolean indicating whether an extension field is required or not
  isExtensionRequired(extensionName: string): boolean {
    return this.getAllFormInputs().some(input => input.name === extensionName && input.isRequired);
  }

  // Returns true if at least one input in the given demographicGroup is required
  isDemographicGroupRequired(demographicGroup: string): boolean {
    return this.getFormInputsByDemographicGroup(demographicGroup).some(input => input.isRequired);
  }

  // Load form select options from ecaSetupConfig
  loadFormSelectOptionsFromSetupConfig(): void {
    this.setupConfig.getFormSelectOptions().subscribe((selectOptions) => {
      this.formSelectOptions = selectOptions;
    });
  }

  // Return form select options for a given field
  getFormSelectOptionsForField(fieldName: string): FormSelectOption[] {
    if (this.formSelectOptions && this.formSelectOptions[fieldName]) {
      return this.formSelectOptions[fieldName];
    }

    return [];
  }

  // This method builds all the form controls
  buildForm() {
    // if the tstTestDate exists on the test, we require it only when some answer bubbles have been filled, but if not, we
    //  still want to constrain the date to be >SCANNING_CREATION_DATE condition
    const tstTestDateValidator = this.isAnyTestPartAttempted() ? makeTestDateValidator(true, 'tst') : makeTestDateValidator(false, 'tst');

    this.form = this.builder.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      stateStudentId: ['', Validators.required],
      gender: ['', GenderValidator],
      birthDate: ['', [Validators.required, BirthDateValidator]],

      tstTestDate: ['', this.hasTstTestDate() ? tstTestDateValidator : ''],
      tstFormSpecifier: ['', this.hasFormSpecifier('tst') ? ChildSpecifierValidator : ''],
      tstLevelSpecifier: ['', this.hasLevelSpecifier('tst') ? ChildSpecifierValidator : ''],

      readingTestDate: ['', this.hasTstTestDate() ? '' : makeTestDateValidator(this.isTestPartAttempted('READING'), 'rd')],
      rdFormSpecifier: ['', this.hasFormSpecifier('rd') && this.isTestPartAttempted('READING') ? ChildSpecifierValidator : ''],
      rdLevelSpecifier: ['', this.hasLevelSpecifier('rd') && this.isTestPartAttempted('READING') ? ChildSpecifierValidator : ''],

      mathTestDate: ['', this.hasTstTestDate() ? '' : makeTestDateValidator(this.isTestPartAttempted('MATH'), 'md')],
      mdFormSpecifier: ['', this.hasFormSpecifier('md') && this.isTestPartAttempted('MATH') ? ChildSpecifierValidator : ''],
      mdLevelSpecifier: ['', this.hasLevelSpecifier('md') && this.isTestPartAttempted('MATH') ? ChildSpecifierValidator : ''],

      languageTestDate: ['', this.hasTstTestDate() ? '' : makeTestDateValidator(this.isTestPartAttempted('LANGUAGE'), 'la')],
      laFormSpecifier: ['', this.hasFormSpecifier('la') && this.isTestPartAttempted('LANGUAGE') ? ChildSpecifierValidator : ''],
      laLevelSpecifier: ['', this.hasLevelSpecifier('la') && this.isTestPartAttempted('LANGUAGE') ? ChildSpecifierValidator : ''],

      listeningTestDate: ['', this.hasTstTestDate() ? '' : makeTestDateValidator(this.isTestPartAttempted('LISTENING'), 'li')],
      liFormSpecifier: ['', this.hasFormSpecifier('li') && this.isTestPartAttempted('LISTENING') ? ChildSpecifierValidator : ''],
      liLevelSpecifier: ['', this.hasLevelSpecifier('li') && this.isTestPartAttempted('LISTENING') ? ChildSpecifierValidator : ''],

      speakingTestDate: ['', this.hasTstTestDate() ? '' : makeTestDateValidator(this.isTestPartAttempted('SPEAKING'), 'sp')],
      spFormSpecifier: ['', this.hasFormSpecifier('sp') && this.isTestPartAttempted('SPEAKING') ? ChildSpecifierValidator : ''],
      spLevelSpecifier: ['', this.hasLevelSpecifier('sp') && this.isTestPartAttempted('SPEAKING') ? ChildSpecifierValidator : ''],

      writingTestDate: ['', this.hasTstTestDate() ? '' : makeTestDateValidator(this.isTestPartAttempted('WRITING'), 'wr')],
      wrFormSpecifier: ['', this.hasFormSpecifier('wr') && this.isTestPartAttempted('WRITING') ? ChildSpecifierValidator : ''],
      wrLevelSpecifier: ['', this.hasLevelSpecifier('wr') && this.isTestPartAttempted('WRITING') ? ChildSpecifierValidator : ''],
    });

    // The following fields are used for TABE 11, 12, 13 & 14 forms
    const singleSelectExtensionFields = [
      'Ethnicity_HispanicLatino_Paper',
      'Highest_Level_Education_Paper',
      'Employment_Status_Paper',
      'PublicAssistance_Paper'
    ];

    singleSelectExtensionFields.forEach((extensionField) => {
      this.form.addControl(extensionField, new FormControl('', this.isExtensionRequired(extensionField) ? Validators.required : null));
    });

    const multiSelectExtensionFields = [
      {
        demographicGroup: 'race',
        fieldName: 'races'
      },
      {
        demographicGroup: 'program',
        fieldName: 'programs'
      }
    ];

    multiSelectExtensionFields.forEach((extensionField) => {
      this.form.addControl(extensionField.fieldName, new FormControl('', this.isDemographicGroupRequired(extensionField.demographicGroup) ? Validators.required : null));
    });
  }

  isTestPartDateOnForm(testPart: string): boolean {
    return !this.hasTstTestDate() && this.isTestPartOnForm(testPart);
  }

  isTestPartOnForm(testPart: string): boolean {
    return !!this.pending.testParts[testPart];
  }

  hasTstTestDate(): boolean {
    return this.pending.tstTestDate !== undefined;
  }

  hasFormSpecifier(subjectPrefix: string): boolean {
    return !!this.pending[`${subjectPrefix}FormSpecifier`];
  }

  hasLevelSpecifier(subjectPrefix: string): boolean {
    return !!this.pending[`${subjectPrefix}LevelSpecifier`];
  }

  isTestPartAttempted(testPart: string) {
    return this.isTestPartOnForm(testPart) && this.pending.testParts[testPart].attempted;
  }

  isAnyTestPartAttempted() {
    return Object.values(this.pending.testParts).filter(testPart => testPart.attempted).length > 0;
  }

  isSaveDisabled(): boolean {
    return this.form.pristine || this.form.invalid;
  }

  getGenderDisplayName(genderValue: string): string {
    return GENDER_MAP.get(genderValue);
  }

  updateBioPatchExtensions(bioPatch: IBiographicPatch): IBiographicPatch {
    // Extract extensions from top-level properties and place them in the extensions array
    if (this.pending.extensions) {
      const raceExtensionNames = this.getExtensionNamesInDemographicGroup('race');
      const programExtensionNames = this.getExtensionNamesInDemographicGroup('program');

      bioPatch.extensions = this.pending.extensions.map((extension) => {
        // Check if this extension is one of the race extensions, if so we need to map it back from the race multiselect dropdown array
        if (raceExtensionNames.includes(extension.name)) {
          return {
            name: extension.name,
            value: bioPatch.races.includes(extension.name) ? 'Y' : null
          }
        }

        // Check if this extension is one of the program extensions, if so we need to map it back from the program multiselect dropdown array
        if (programExtensionNames.includes(extension.name)) {
          return {
            name: extension.name,
            value: bioPatch.programs.includes(extension.name) ? 'Y' : null
          }
        }

        if (bioPatch[extension.name]) {
          extension.value = bioPatch[extension.name];

          // Remove the extension from the top-level bioPatch as we don't need to pass it to the backend
          delete bioPatch[extension.name];
        }

        return extension;
      });
    }

    // Remove the multi-select races and programs properties as we don't need to pass them to the backend
    delete bioPatch.races;
    delete bioPatch.programs;

    return bioPatch;
  }

  onSubmit() {
    if (!this.form.valid) {
      return;
    }

    this.spinnerService.show();

    let bioPatch: IBiographicPatch = {
      ...this.form.value,
    };

    // Update the extensions array in the bioPatch object
    bioPatch = this.updateBioPatchExtensions(bioPatch);

    // convert back to MM/DD/YYYY
    this.formToStringDates(bioPatch, [
      'tstTestDate',
      'readingTestDate',
      'languageTestDate',
      'mathTestDate',
      'speakingTestDate',
      'listeningTestDate',
      'writingTestDate',
      'birthDate',
    ]);

    this.api.patchPending(this.pendingId, bioPatch)
      .pipe(
        take(1),
        catchError((error) => {
          console.log(error);
          return null;
        }),
        filterEmptyValue(),
        finalize(() => {
          this.spinnerService.hide();
        }),
      )
      .subscribe(() => {
        this.pendingCollection.updateWithBiographicPatch(this.pending, bioPatch);
        this.pendingCollection.syncPending();
        void this.router.navigate([this.previousUrl]);
      });
  }

  formToStringDates(data: {}, keys: string[]) {
    keys.forEach(key => {
      try {
        // do not create a new date from a NULL value. Leave it empty(NULL).
        if (data[key]) {
          const date = new Date(data[key]);
          data[key] = (date.getMonth() + 1) + '/' + date.getDate() + '/' + date.getFullYear();
        } else {
          data[key] = null;
        }
      } catch (e) {
        data[key] = null;
      }
    });
  }

  formatFormDates(keys: string[], biographic: IBiographic) {
    keys.forEach(key => {
      try {
        // do not create a Date value from a NULL value. Leave it empty.
        if (biographic[key]) {
          biographic[key] = new Date(biographic[key]);
        }
      } catch (e) {
        biographic[key] = null;
      }
    });
  }

  getFormattedBiographic(pending: Pending) {
    const formattedBiographic: IBiographic = {...pending};

    // When some fields are not bubbled on a form, local scanning service can use * or - to denote a no-response.
    const noResponseValues = ['*', '-'];

    // Apply extensions to the top-level properties of the formattedBiographic
    if (pending.extensions && pending.extensions.length > 0) {
      const raceExtensionNames = this.getExtensionNamesInDemographicGroup('race');
      const programExtensionNames = this.getExtensionNamesInDemographicGroup('program');

      formattedBiographic.races = [];
      formattedBiographic.programs = [];

      pending.extensions.forEach((extension) => {
        if (raceExtensionNames.includes(extension.name) && extension.value?.toUpperCase() === 'Y') {
          formattedBiographic.races.push(extension.name);
        } else if (programExtensionNames.includes(extension.name) && extension.value?.toUpperCase() === 'Y') {
          formattedBiographic.programs.push(extension.name);
        } else {
          formattedBiographic[extension.name] = (noResponseValues.includes(extension.value)) ? null : extension.value;
        }
      });
    }

    this.formatFormDates([
      'tstTestDate',
      'readingTestDate',
      'languageTestDate',
      'mathTestDate',
      'speakingTestDate',
      'listeningTestDate',
      'writingTestDate',
      'birthDate',
    ], formattedBiographic);

    return formattedBiographic;
  }
}
